import { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Typography,
} from '@material-ui/core';
import { bool, string } from 'prop-types';
import Client, { SERVICES } from 'services/dynamipay';

const MIN_AMOUNT = 500;

function MSIControl(props) {
  const {
    disabled,
    error,
    helperText,
    amountField = 'amount',
  } = props;

  const { values, setFieldValue } = useFormikContext();

  const [msiOptions, setMsiOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMsiOptions() {
      try {
        const response = await Client(SERVICES.SERVICE_MSI).get();
        const msiData = response.values?.msi || response.values || {};
        const options = Object.entries(msiData).map(([months, minAmount]) => ({
          months: parseInt(months),
          minAmount: parseInt(minAmount),
        }));
        setMsiOptions(options);
      } catch (err) {
        console.error('Error fetching MSI options:', err);
        setMsiOptions([]);
      } finally {
        setLoading(false);
      }
    }
    fetchMsiOptions();
  }, []);

  const amount = parseFloat(values[amountField]) || 0;
  const amountQualifies = amount >= MIN_AMOUNT;
  const switchOn = values?.config?.msi?.enabled || false;
  const selectedMonths = values?.config?.msi?.months || [];

  // Si el monto baja de $500 y el switch estaba encendido, apagarlo
  useEffect(() => {
    if (!amountQualifies && switchOn) {
      setFieldValue('config.msi.enabled', false);
      setFieldValue('config.msi.months', []);
    }
  }, [amountQualifies]);

  const handleToggle = () => {
    if (disabled || !amountQualifies) return;
    const next = !switchOn;
    setFieldValue('config.msi.enabled', next);
    if (!next) setFieldValue('config.msi.months', []);
  };

  const availableOptions = msiOptions.filter((option) => amount >= option.minAmount);

  const handleMonthsChange = (months) => {
    const currentMonths = [...selectedMonths];
    const index = currentMonths.indexOf(months);
    if (index === -1) currentMonths.push(months);
    else currentMonths.splice(index, 1);
    currentMonths.sort((a, b) => a - b);
    setFieldValue('config.msi.months', currentMonths);
  };

  return (
    <Box>
      <FormControl component="fieldset" disabled={disabled} error={error} fullWidth>

        <Box display="flex" alignItems="center" style={{
          gap: 8,
          marginBottom: 4,
        }}>
          <Box
            onClick={handleToggle}
            style={{
              width: 44,
              height: 24,
              borderRadius: 9999,
              backgroundColor: switchOn ? '#1E8E5E' : '#D1D5DC',
              display: 'flex',
              alignItems: 'center',
              padding: '0 3px',
              cursor: disabled || !amountQualifies ? 'default' : 'pointer',
              transition: 'background-color 0.2s ease',
              flexShrink: 0,
              opacity: !amountQualifies ? 0.5 : 1,
            }}
          >
            <Box style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.2)',
              transform: switchOn ? 'translateX(20px)' : 'translateX(0px)',
              transition: 'transform 0.2s ease',
            }} />
          </Box>
          <Typography style={{
            fontSize: 14,
            color: '#101828',
          }}>
            Habilitar meses sin intereses
          </Typography>
        </Box>

        {!amountQualifies && (
          <Typography style={{
            fontSize: 13,
            color: '#4A5565',
            marginTop: 4,
          }}>
            El monto mínimo para ofrecer meses sin intereses es de $500 MXN
          </Typography>
        )}

        {amountQualifies && switchOn && (
          <Box mt={1}>
            {loading ? (
              <Box display="flex" alignItems="center" style={{
                gap: 8,
              }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="textSecondary">Cargando opciones...</Typography>
              </Box>
            ) : availableOptions.length > 0 && (
              <>
                <Typography style={{
                  fontSize: 13,
                  color: '#4A5565',
                  marginBottom: 12,
                }}>
                  Selecciona las mensualidades disponibles
                </Typography>
                <Box display="flex" style={{
                  gap: 8,
                  flexWrap: 'wrap',
                }}>
                  {availableOptions.map((option) => {
                    const selected = selectedMonths.includes(option.months);
                    return (
                      <Box
                        key={option.months}
                        onClick={() => !disabled && handleMonthsChange(option.months)}
                        style={{
                          width: 64,
                          height: 64,
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: selected ? '2px solid #1E8E5E' : '1px solid #D1D5DC',
                          borderRadius: 10,
                          cursor: disabled ? 'default' : 'pointer',
                          backgroundColor: selected ? '#F0FDF4' : '#FFFFFF',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {selected && (
                          <span style={{
                            position: 'absolute',
                            top: 4,
                            left: 6,
                            fontSize: 10,
                            color: '#1E8E5E',
                            fontWeight: 700,
                          }}>✓</span>
                        )}
                        <Typography style={{
                          fontSize: 18,
                          fontWeight: 600,
                          color: selected ? '#1E8E5E' : '#101828',
                          lineHeight: 1,
                        }}>
                          {option.months}
                        </Typography>
                        <Typography style={{
                          fontSize: 11,
                          color: selected ? '#1E8E5E' : '#6A7282',
                          marginTop: 4,
                        }}>
                          meses
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        )}

        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
}

MSIControl.propTypes = {
  amountField: string,
  disabled: bool,
  error: bool,
  helperText: string,
  name: string,
};

MSIControl.defaultProps = {
  amountField: 'amount',
  disabled: false,
  error: false,
  name: 'config.msi',
};

export default MSIControl;
